/**
 * Crisp Configuration Utility
 *
 * Centralized configuration for Crisp chat widget
 * Uses environment variables for security and flexibility
 */

import { Crisp } from "crisp-sdk-web";
import { createLogger } from "./logger";

const logger = createLogger("CrispConfig");

// Default Crisp configuration options
const DEFAULT_CONFIG = {
  autoload: true,
  lockMaximized: false,
  lockFullview: false
};

/**
 * Configure Crisp chat widget
 * @param {Object} customConfig - Custom configuration options to override defaults
 * @returns {boolean} - Whether Crisp was successfully configured
 */
export const configureCrisp = (customConfig = {}) => {
  try {
    // Get Crisp website ID from environment variables
    const crispWebsiteId = process.env.REACT_APP_CRISP_WEBSITE_ID;

    if (!crispWebsiteId) {
      logger.warn(
        "⚠️ REACT_APP_CRISP_WEBSITE_ID not found in environment variables. Crisp chat will not be initialized."
      );
      return false;
    }

    // Merge default config with custom config
    const config = {
      ...DEFAULT_CONFIG,
      ...customConfig
    };

    // Configure Crisp
    Crisp.configure(crispWebsiteId, config);

    logger.log("✅ Crisp chat widget configured successfully");
    return true;
  } catch (error) {
    logger.error("❌ Failed to configure Crisp chat widget:", error);
    return false;
  }
};

/**
 * Configure Crisp with locked interface (for support pages)
 * @param {Object} customConfig - Custom configuration options
 * @returns {boolean} - Whether Crisp was successfully configured
 */
export const configureCrispLocked = (customConfig = {}) => {
  return configureCrisp({
    lockMaximized: true,
    lockFullview: true,
    ...customConfig
  });
};

/**
 * Check if Crisp is properly configured
 * @returns {boolean} - Whether Crisp is configured and ready
 */
export const isCrispConfigured = () => {
  try {
    // Check if Crisp is available and configured
    return (
      typeof window !== "undefined" &&
      window.Crisp &&
      window.Crisp.is("chat:visible") !== undefined
    );
  } catch (_error) {
    return false;
  }
};

/**
 * Get current Crisp configuration status
 * @returns {Object} - Configuration status information
 */
export const getCrispStatus = () => {
  return {
    configured: isCrispConfigured(),
    websiteId: process.env.REACT_APP_CRISP_WEBSITE_ID || null,
    environment: process.env.NODE_ENV || "development"
  };
};
